import { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { Link } from '@tanstack/react-router'
import { AppLayout } from '../AppLayout'
import { TopBarBackIconButton } from '../topbar/TopBarBackIconButton'
import { DesktopPage } from '../desktop/DesktopPage'
import { DesktopPageHeader } from '../desktop/DesktopPageHeader'
import { useIsDesktop } from '../ui/use-mobile'
import { ListSkeleton } from '../ui/skeleton-presets'
import { EmptyState } from '../EmptyState'
import { FileClock } from '../icons'
import { cn } from '@/lib/utils'
import { useAuditLogEntityTypes, useAuditLogList } from '@/lib/hooks/audit'
import { formatAuditAction, formatEntityType, type AuditLogEntry } from '@/lib/audit-format'

// Admin activity feed: who did what, newest first, grouped by day. Data is
// recorded server-side by the audit middleware (apps/api/src/lib/audit).
export function ActivityLog() {
  const isDesktop = useIsDesktop()
  const [entityType, setEntityType] = useState<string | undefined>(undefined)

  const { entityTypes } = useAuditLogEntityTypes()
  const {
    entries,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAuditLogList(entityType ? { entityType } : {})

  // Group consecutive entries by calendar day (list is already newest-first).
  const dayGroups = useMemo(() => {
    const groups: { day: string; entries: AuditLogEntry[] }[] = []
    for (const entry of entries) {
      const day = dayjs(entry.createdAt).format('YYYY-MM-DD')
      const lastGroup = groups[groups.length - 1]
      if (lastGroup?.day === day) lastGroup.entries.push(entry)
      else groups.push({ day, entries: [entry] })
    }
    return groups
  }, [entries])

  // Infinite scroll: fetch the next page when the sentinel enters the viewport.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage) return
    const observer = new IntersectionObserver(
      (observed) => {
        if (observed[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage()
      },
      { rootMargin: '200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const filterChips = entityTypes.length > 1 && (
    <div className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none]">
      <FilterChip label="All" active={!entityType} onClick={() => setEntityType(undefined)} />
      {entityTypes.map((type) => (
        <FilterChip
          key={type}
          label={formatEntityType(type)}
          active={entityType === type}
          onClick={() => setEntityType(type)}
        />
      ))}
    </div>
  )

  const body = (
    <div className="flex flex-col gap-4">
      {filterChips}

      {isLoading ? (
        <ListSkeleton rows={8} />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={FileClock}
          title="No activity yet"
          description="Changes made by your team will show up here."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {dayGroups.map((group) => (
            <section key={group.day} className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-[1.5px] text-content-primary-subtle-mid">
                {formatDayHeader(group.day)}
              </p>
              <div className="bg-surface-L1 border border-border-L1 rounded-2xl px-4">
                {group.entries.map((entry, index) => (
                  <div key={entry.id}>
                    {index > 0 && <div className="h-px bg-border-L1" />}
                    <ActivityRow entry={entry} />
                  </div>
                ))}
              </div>
            </section>
          ))}

          <div ref={sentinelRef} />
          {isFetchingNextPage && <ListSkeleton rows={3} />}
        </div>
      )}
    </div>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Activity Log"
          breadcrumb={
            <span>
              <Link to="/operations" className="hover:underline">
                Operations
              </Link>
              <span className="text-content-primary-subtle"> / Activity Log</span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          showBackButton
          title="Activity Log"
          className="bg-surface-brand-L0"
        />
      }
      className="pt-4 bg-surface-brand-L0"
    >
      {body}
    </AppLayout>
  )
}

function ActivityRow({ entry }: { entry: AuditLogEntry }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-3">
      <p className="text-sm text-content-primary-bold min-w-0">
        <span className="font-semibold">{entry.actorName}</span>{' '}
        <span className="text-content-primary-subtle">{formatAuditAction(entry)}</span>
      </p>
      <p className="shrink-0 text-xs text-content-primary-subtle-mid">
        {dayjs(entry.createdAt).format('HH:mm')}
      </p>
    </div>
  )
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex h-8 shrink-0 items-center rounded-full px-3 text-xs font-semibold transition-colors',
        active
          ? 'bg-background-primary-bold text-content-primary-inverse'
          : 'bg-content-primary-subtle-light text-content-primary-subtle'
      )}
    >
      {label}
    </button>
  )
}

function formatDayHeader(day: string): string {
  const date = dayjs(day)
  if (date.isSame(dayjs(), 'day')) return 'Today'
  if (date.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday'
  return date.format('D MMMM YYYY')
}
